import { useEffect, useRef, useState } from "react";
import api, { formatApiError, photoUrl, formatMartiniqueDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Lock, Trophy, Loader2, Sailboat, Anchor, Download, Users, Copy, ShieldCheck, Archive, PlayCircle, History, ImagePlus, UserCircle, Save, KeyRound, DatabaseBackup, RotateCcw, Radio, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { FanionBadge } from "@/components/FanionBadge";

export default function Admin() {
  const [tab, setTab] = useState("leagues");
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--coral)]">Panneau admin</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[color:var(--deep)]">
          Gestion du tour
        </h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 rounded-full h-auto flex-wrap gap-1">
          <TabsTrigger value="leagues" data-testid="admin-tab-leagues" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Users className="h-3.5 w-3.5 mr-1.5" /> Ligues
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <UserCircle className="h-3.5 w-3.5 mr-1.5" /> Profils
          </TabsTrigger>
          <TabsTrigger value="yoles" data-testid="admin-tab-yoles" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Anchor className="h-3.5 w-3.5 mr-1.5" /> Yoles
          </TabsTrigger>
          <TabsTrigger value="stages" data-testid="admin-tab-stages" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Sailboat className="h-3.5 w-3.5 mr-1.5" /> Étapes
          </TabsTrigger>
          <TabsTrigger value="season" data-testid="admin-tab-season" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Trophy className="h-3.5 w-3.5 mr-1.5" /> Podium final
          </TabsTrigger>
          <TabsTrigger value="seasons" data-testid="admin-tab-seasons" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <History className="h-3.5 w-3.5 mr-1.5" /> Saisons
          </TabsTrigger>
          <TabsTrigger value="backups" data-testid="admin-tab-backups" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <DatabaseBackup className="h-3.5 w-3.5 mr-1.5" /> Sauvegardes
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Radio className="h-3.5 w-3.5 mr-1.5" /> Live
          </TabsTrigger>
        </TabsList>
        <TabsContent value="leagues" className="mt-6"><LeaguesAdmin /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersAdmin /></TabsContent>
        <TabsContent value="yoles" className="mt-6"><YolesAdmin /></TabsContent>
        <TabsContent value="stages" className="mt-6"><StagesAdmin /></TabsContent>
        <TabsContent value="season" className="mt-6"><SeasonAdmin /></TabsContent>
        <TabsContent value="seasons" className="mt-6"><SeasonsAdmin /></TabsContent>
        <TabsContent value="backups" className="mt-6"><BackupsAdmin /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [yoles, setYoles] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);   // user being reset (loading)
  const [resetResult, setResetResult] = useState(null); // {email, temporary_password}
  const [deletingUser, setDeletingUser] = useState(null); // user pending confirm
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [u, l, y] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/leagues"),
        api.get("/yoles"),
      ]);
      setUsers(u.data);
      setLeagues(l.data);
      setYoles(y.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (
      u.name?.toLowerCase().includes(needle) ||
      u.email?.toLowerCase().includes(needle) ||
      u.league_name?.toLowerCase().includes(needle)
    );
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        email: editing.email,
        league_id: editing.league_id ?? "",
        favorite_yole_id: editing.favorite_yole_id ?? "",
        wallet_points: Number(editing.wallet_points) || 0,
        role: editing.role,
      };
      await api.put(`/admin/users/${editing.id}`, payload);
      toast.success("Profil mis à jour");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeUser = async (u) => {
    // Open Shadcn AlertDialog (nicer than window.confirm)
    setDeletingUser(u);
  };

  const confirmRemoveUser = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deletingUser.id}`);
      toast.success(`Profil « ${deletingUser.name} » supprimé`);
      setDeletingUser(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const resetPassword = async (u) => {
    if (!confirm(`Générer un nouveau mot de passe temporaire pour ${u.name} (${u.email}) ? Il sera affiché une seule fois — pense à le transmettre en sécurité.`)) return;
    setResetting(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      setResetResult({ email: data.user_email, password: data.temporary_password, name: u.name });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetting(null); }
  };

  const copyPassword = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      toast.success("Mot de passe copié");
    } catch { toast.error("Copie impossible"); }
  };

  return (
    <div className="space-y-4" data-testid="users-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-500">
          <span className="font-bold text-[color:var(--deep)]">{users.length}</span> profil{users.length > 1 ? "s" : ""} enregistré{users.length > 1 ? "s" : ""}
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher (nom, email, ligue)"
          data-testid="users-search"
          className="h-10 rounded-full max-w-xs bg-white"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_1.5fr_1fr_0.7fr_0.6fr_auto] gap-4 px-4 py-3 border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
          <div>Nom</div>
          <div>Email</div>
          <div>Ligue</div>
          <div>Points</div>
          <div>Fanion</div>
          <div className="text-right">Actions</div>
        </div>
        {filtered.map((u) => (
          <div
            key={u.id}
            className="grid grid-cols-2 md:grid-cols-[1fr_1.5fr_1fr_0.7fr_0.6fr_auto] gap-x-4 gap-y-1 px-4 py-3 border-b border-slate-50 hover:bg-slate-50/60 items-center text-sm"
            data-testid={`user-row-${u.id}`}
          >
            <div className="font-bold text-[color:var(--deep)] flex items-center gap-2 min-w-0">
              <FanionBadge level={u.fanion_level || 1} size="sm" />
              <span className="truncate">{u.name}</span>
              {u.role === "admin" && <span className="text-[9px] font-black uppercase text-[color:var(--coral)] bg-red-50 border border-red-100 rounded-full px-1.5 py-0.5">Admin</span>}
            </div>
            <div className="text-xs text-slate-500 truncate col-span-2 md:col-span-1">{u.email}</div>
            <div className="text-xs text-slate-600 truncate">{u.league_name || <span className="text-slate-400">—</span>}</div>
            <div className="font-mono font-bold text-[color:var(--deep)] tabular-nums">{u.wallet_points}</div>
            <div className="text-xs text-slate-600 truncate">{u.fanion_label}</div>
            <div className="flex gap-1 justify-end col-span-2 md:col-span-1">
              <button
                onClick={() => setEditing({ ...u, league_id: u.league_id || "", favorite_yole_id: u.favorite_yole_id || "" })}
                data-testid={`user-edit-${u.id}`}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[color:var(--deep)]"
                title="Modifier"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => resetPassword(u)}
                disabled={resetting === u.id}
                data-testid={`user-reset-pw-${u.id}`}
                className="p-2 rounded-lg text-[color:var(--ocean)] hover:bg-cyan-50 disabled:opacity-40"
                title="Réinitialiser le mot de passe"
              >
                {resetting === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              </button>
              <button
                onClick={() => removeUser(u)}
                data-testid={`user-delete-${u.id}`}
                className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                title="Supprimer"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-10 text-slate-400 text-sm">Aucun profil ne correspond</div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">Modifier le profil</DialogTitle>
            <DialogDescription className="text-slate-500">
              Mets à jour le nom, l'email, les points, le rôle ou la ligue d'un profil enregistré.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Prénom</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-edit-name" className="h-11 rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Email</Label>
                  <Input value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-edit-email" className="h-11 rounded-xl mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Points</Label>
                  <Input type="number" min="0" value={editing.wallet_points} onChange={(e) => setEditing({ ...editing, wallet_points: e.target.value })} data-testid="user-edit-points" className="h-11 rounded-xl mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Rôle</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                    <SelectTrigger data-testid="user-edit-role" className="h-11 rounded-xl mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="user">Utilisateur</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Ligue</Label>
                <Select value={editing.league_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, league_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="user-edit-league" className="h-11 rounded-xl mt-1 bg-white"><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__none__">Aucune ligue</SelectItem>
                    {leagues.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Yole favorite</Label>
                <Select value={editing.favorite_yole_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, favorite_yole_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="user-edit-favorite" className="h-11 rounded-xl mt-1 bg-white"><SelectValue placeholder="Aucune" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__none__">Aucune</SelectItem>
                    {yoles.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="user-edit-save" className="rounded-full btn-cta h-11">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Enregistrer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset result — displayed ONCE */}
      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="user-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[color:var(--ocean)]" /> Mot de passe temporaire
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Ce mot de passe ne sera pas affiché à nouveau. Copie-le et transmets-le à {resetResult?.name} par un canal sécurisé (WhatsApp, SMS…).
            </DialogDescription>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-3">
              <div className="rounded-xl bg-[color:var(--seafoam)] border border-slate-200 p-4">
                <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Email du compte</div>
                <div className="font-mono text-sm text-[color:var(--deep)] mt-0.5" data-testid="reset-user-email">{resetResult.email}</div>
              </div>
              <div className="rounded-xl bg-[color:var(--deep)] text-white p-4">
                <div className="text-[10px] uppercase font-bold tracking-widest text-[color:var(--sunshine)]">Nouveau mot de passe</div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <code className="font-mono text-lg font-bold tracking-wider text-white select-all" data-testid="reset-new-password">
                    {resetResult.password}
                  </code>
                  <Button
                    onClick={copyPassword}
                    data-testid="reset-copy-btn"
                    className="rounded-full h-9 px-3 bg-[color:var(--sunshine)] hover:bg-[color:var(--sunshine)]/90 text-[color:var(--deep)] font-bold text-xs"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copier
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <strong>Rappel</strong> : demande à l'utilisateur de le changer dès sa prochaine connexion.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} data-testid="reset-close-btn" className="rounded-full h-11 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation modale de suppression d'utilisateur (Shadcn) */}
      <AlertDialog open={!!deletingUser} onOpenChange={(v) => !v && !deleting && setDeletingUser(null)}>
        <AlertDialogContent className="bg-white max-w-md" data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-extrabold text-[color:var(--coral)] flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Supprimer ce profil ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 space-y-2">
              {deletingUser && (
                <>
                  <div>
                    Tu vas supprimer <b>{deletingUser.name}</b> ({deletingUser.email}).
                  </div>
                  <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2 mt-2">
                    <b>Attention :</b> tous ses pronostics, ses paris champion et ses notifications
                    seront définitivement effacés. Cette action est irréversible.
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="user-delete-cancel" className="rounded-full">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmRemoveUser(); }}
              data-testid="user-delete-confirm"
              className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function BackupsAdmin() {
  const [backups, setBackups] = useState([]);
  const [config, setConfig] = useState({ retention: 14, interval_hours: 12, backup_dir: "" });
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/backup/list");
      setBackups(data.backups || []);
      setConfig({ retention: data.retention, interval_hours: data.interval_hours, backup_dir: data.backup_dir });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backup/run");
      toast.success("Sauvegarde créée");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (name) => {
    try {
      const token = localStorage.getItem("yoles_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/download/${encodeURIComponent(name)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Téléchargement impossible");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(String(e.message || e)); }
  };

  const remove = async (name) => {
    if (!confirm(`Supprimer la sauvegarde ${name} ? (Une nouvelle sauvegarde est créée toutes les ${config.interval_hours}h automatiquement.)`)) return;
    try {
      await api.delete(`/admin/backup/${encodeURIComponent(name)}`);
      toast.success("Sauvegarde supprimée");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (name) => {
    if (!confirm(`⚠️ RESTAURATION\n\nRestaurer la sauvegarde ${name} va REMPLACER toutes les données actuelles (utilisateurs, ligues, pronostics…) par celles de la sauvegarde. Une sauvegarde de sécurité de l'état actuel sera créée automatiquement juste avant.\n\nContinuer ?`)) return;
    setRestoring(name);
    try {
      const { data } = await api.post(`/admin/backup/restore/${encodeURIComponent(name)}`);
      toast.success(`Restauration réussie (snapshot avant : ${data.pre_restore_snapshot})`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(null); }
  };

  const humanSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
  };

  return (
    <div className="space-y-4" data-testid="backups-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500">
          <span className="font-bold text-[color:var(--deep)]">{backups.length}</span> sauvegarde{backups.length > 1 ? "s" : ""}
          <span className="mx-2">·</span>
          auto toutes les <span className="font-bold text-[color:var(--deep)]">{config.interval_hours}h</span>
          <span className="mx-2">·</span>
          rétention <span className="font-bold text-[color:var(--deep)]">{config.retention}</span> dernières
        </div>
        <Button onClick={runNow} disabled={busy} data-testid="backup-run" className="rounded-full btn-cta h-10">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DatabaseBackup className="h-4 w-4 mr-1.5" />}
          Créer une sauvegarde
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_auto] gap-4 px-4 py-3 border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
          <div>Fichier</div>
          <div>Date</div>
          <div>Taille</div>
          <div className="text-right">Actions</div>
        </div>
        {backups.length === 0 && (
          <div className="text-center py-10 text-slate-400 text-sm">Aucune sauvegarde pour l'instant</div>
        )}
        {backups.map((b) => (
          <div
            key={b.name}
            className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-x-4 gap-y-1 px-4 py-3 border-b border-slate-50 items-center text-sm"
            data-testid={`backup-row-${b.name}`}
          >
            <div className="font-mono text-[color:var(--deep)] truncate">{b.name}</div>
            <div className="text-xs text-slate-600 font-mono">{b.created_at ? new Date(b.created_at).toLocaleString("fr-FR") : "—"}</div>
            <div className="text-xs text-slate-600 font-mono">{humanSize(b.size)}</div>
            <div className="flex gap-1 justify-end">
              <button
                onClick={() => download(b.name)}
                data-testid={`backup-download-${b.name}`}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[color:var(--deep)]"
                title="Télécharger"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                onClick={() => restore(b.name)}
                disabled={restoring === b.name}
                data-testid={`backup-restore-${b.name}`}
                className="p-2 rounded-lg text-[color:var(--ocean)] hover:bg-cyan-50 disabled:opacity-40"
                title="Restaurer"
              >
                {restoring === b.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              </button>
              <button
                onClick={() => remove(b.name)}
                data-testid={`backup-delete-${b.name}`}
                className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                title="Supprimer"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
        <strong>Note</strong> : la restauration remplace l'intégralité de la base par le contenu de la sauvegarde. Une sauvegarde de sécurité automatique est créée juste avant chaque restauration, au cas où tu voudrais annuler.
      </div>
    </div>
  );
}


function SeasonsAdmin() {
  const [seasons, setSeasons] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`Tour ${new Date().getFullYear() + 1}`);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/seasons");
    setSeasons(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = async () => {
    if (!confirm(`Démarrer une nouvelle saison "${name}" ? La saison en cours sera archivée et tous les soldes remis à 1000 points.`)) return;
    setLoading(true);
    try {
      await api.post("/admin/seasons", { name });
      toast.success("Nouvelle saison lancée !");
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const removeSeason = async (id) => {
    if (!confirm("Supprimer cette saison archivée et toutes ses données (étapes, paris) ?")) return;
    try {
      await api.delete(`/admin/seasons/${id}`);
      toast.success("Saison supprimée");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-500">
          {seasons.length} saison{seasons.length > 1 ? "s" : ""} · Toutes les yoles/étapes/paris sont rattachés à une saison
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-season-btn" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-10">
              <PlayCircle className="h-4 w-4 mr-1.5" /> Nouvelle saison
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold">Démarrer une saison</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Nom de la saison</Label>
                <Input data-testid="season-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" placeholder="Tour 2027" />
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <strong>Attention</strong> : la saison en cours sera archivée. Les soldes de tous les parieurs seront remis à <strong>1000 points</strong>. L'historique reste consultable.
              </div>
            </div>
            <DialogFooter>
              <Button onClick={startNew} disabled={loading || !name.trim()} data-testid="confirm-new-season" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-11">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Démarrer la saison"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {seasons.map((s) => (
          <div key={s.id} className={`bento-tile bg-white border rounded-2xl p-5 ${s.is_current ? "border-[color:var(--sunshine)] shadow-sm" : "border-slate-200"}`} data-testid={`season-card-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {s.is_current ? (
                    <span className="text-[10px] uppercase font-black tracking-widest text-[color:var(--deep)] bg-[color:var(--sunshine)] px-2 py-0.5 rounded-full">Active</span>
                  ) : (
                    <span className="text-[10px] uppercase font-black tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Archivée</span>
                  )}
                  {s.settled && (
                    <span className="text-[10px] uppercase font-black tracking-widest text-[color:var(--palm)] bg-[color:var(--palm)]/15 px-2 py-0.5 rounded-full">Réglée</span>
                  )}
                </div>
                <div className="font-display font-extrabold text-xl text-[color:var(--deep)] mt-1.5">{s.name}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">
                  {s.stages_count} étapes · {s.total_bets} paris · Créée {new Date(s.created_at).toLocaleDateString("fr-FR")}
                  {s.closed_at && ` · Archivée ${new Date(s.closed_at).toLocaleDateString("fr-FR")}`}
                </div>
              </div>
              <div className="flex gap-1">
                {!s.is_current && (
                  <button onClick={() => removeSeason(s.id)} data-testid={`delete-season-${s.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50" title="Supprimer">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {seasons.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
            <Archive className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <div className="font-display font-bold text-lg text-[color:var(--deep)]">Aucune saison</div>
          </div>
        )}
      </div>
    </div>
  );
}

function LeaguesAdmin() {
  const [leagues, setLeagues] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "" });
  const [membersOf, setMembersOf] = useState(null); // {league, users}

  const load = async () => {
    const { data } = await api.get("/admin/leagues");
    setLeagues(data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", code: randomCode() });
    setOpen(true);
  };
  const startEdit = (l) => {
    setEditing(l);
    setForm({ name: l.name, code: l.code });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { name: form.name, code: form.code.toUpperCase().replace(/[^A-Z0-9]/g, "") };
      if (editing) {
        await api.put(`/admin/leagues/${editing.id}`, payload);
        toast.success("Ligue modifiée");
      } else {
        await api.post("/admin/leagues", payload);
        toast.success("Ligue créée");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Supprimer la ligue et tous ses membres/paris ?")) return;
    try {
      const { data } = await api.delete(`/admin/leagues/${id}`);
      toast.success(`Ligue supprimée · ${data.users_removed} membres retirés`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openMembers = async (l) => {
    try {
      const { data } = await api.get(`/admin/leagues/${l.id}/members`);
      setMembersOf({ league: l, users: data });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code copié : ${code}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-slate-500">{leagues.length} ligue{leagues.length > 1 ? "s" : ""} · Chaque ligue = un groupe d'amis qui parie ensemble</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startCreate} data-testid="add-league-btn" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-10">
              <Plus className="h-4 w-4 mr-1" /> Nouvelle ligue
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold">
                {editing ? "Modifier la ligue" : "Nouvelle ligue"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Nom de la ligue</Label>
                <Input data-testid="league-name" placeholder="ex. Bureau Fort-de-France" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Code d'invitation</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    data-testid="league-code"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="h-11 rounded-xl font-mono tracking-widest uppercase"
                    placeholder="TOUR2026"
                  />
                  <Button
                    type="button"
                    onClick={() => setForm({ ...form, code: randomCode() })}
                    variant="outline"
                    className="rounded-xl h-11 text-xs font-bold"
                  >
                    Générer
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">Les utilisateurs entrent ce code à l'inscription pour rejoindre.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-league" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-11">
                {editing ? "Enregistrer" : "Créer la ligue"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {leagues.map((l) => (
          <div key={l.id} className="bento-tile bg-white border border-slate-200 rounded-2xl p-5" data-testid={`league-card-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-[color:var(--coral)]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    {l.member_count} membre{l.member_count > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="font-display font-extrabold text-lg text-[color:var(--deep)] truncate">{l.name}</div>
                <button
                  onClick={() => copyCode(l.code)}
                  data-testid={`league-copy-${l.id}`}
                  className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition"
                >
                  <span className="font-mono font-bold text-sm tracking-widest text-[color:var(--deep)]">{l.code}</span>
                  <Copy className="h-3.5 w-3.5 text-slate-500" />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => openMembers(l)} data-testid={`league-members-${l.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Membres">
                  <Users className="h-4 w-4" />
                </button>
                <button onClick={() => startEdit(l)} data-testid={`league-edit-${l.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Modifier">
                  <Edit className="h-4 w-4" />
                </button>
                <button onClick={() => remove(l.id)} data-testid={`league-delete-${l.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50" title="Supprimer">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {leagues.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
            <Users className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <div className="font-display font-bold text-lg text-[color:var(--deep)]">Aucune ligue</div>
            <p className="text-sm text-slate-500 mt-1">Crée une première ligue et partage son code avec tes amis.</p>
          </div>
        )}
      </div>

      {/* Members dialog */}
      <Dialog open={!!membersOf} onOpenChange={(o) => !o && setMembersOf(null)}>
        <DialogContent className="bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">
              Membres · {membersOf?.league?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            {membersOf?.users?.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">Aucun membre pour l'instant.</div>
            )}
            {membersOf?.users?.map((u) => (
              <div key={u.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                <div className="w-9 h-9 rounded-full bg-[color:var(--coral)]/10 flex items-center justify-center font-bold text-sm text-[color:var(--coral)]">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate flex items-center gap-1.5">
                    {u.name}
                    {u.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--sunshine)]" />}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="font-mono font-bold text-sm tabular-nums text-[color:var(--deep)]">
                  {u.wallet_points}<span className="text-xs opacity-60 ml-1">pts</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function randomCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const DEFAULT_COLORS = ["#FF5A5F", "#00B4D8", "#FFC107", "#A7C957", "#7B3F9F", "#F4A261", "#264653", "#E63946"];

function YolesAdmin() {
  const [yoles, setYoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", sponsor: "", skipper: "", color: DEFAULT_COLORS[0], photo_url: "" });
  const [seeding, setSeeding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/yoles");
    setYoles(data);
  };
  useEffect(() => { load(); }, []);

  const importOfficial = async () => {
    if (!confirm("Importer les yoles et étapes officielles du Tour ? Les doublons seront ignorés.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-official");
      toast.success(`Import réussi · ${data.yoles_added} yoles + ${data.stages_added} étapes ajoutées`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", sponsor: "", skipper: "", color: DEFAULT_COLORS[0], photo_url: "" });
    setOpen(true);
  };
  const startEdit = (y) => {
    setEditing(y);
    setForm({ name: y.name, sponsor: y.sponsor || "", skipper: y.skipper || "", color: y.color, photo_url: y.photo_url || "" });
    setOpen(true);
  };
  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop lourde (5 Mo max)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/yoles/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_url: data.photo_url }));
      toast.success("Photo uploadée");
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/yoles/${editing.id}`, form);
        toast.success("Yole mise à jour");
      } else {
        await api.post("/admin/yoles", form);
        toast.success("Yole créée");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Supprimer cette yole ?")) return;
    try {
      await api.delete(`/admin/yoles/${id}`);
      toast.success("Yole supprimée");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{yoles.length} yoles enregistrées</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startCreate} data-testid="add-yole-btn" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-10">
              <Plus className="h-4 w-4 mr-1" /> Ajouter une yole
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold">
                {editing ? "Modifier la yole" : "Nouvelle yole"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Nom</Label>
                <Input data-testid="yole-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Sponsor</Label>
                <Input data-testid="yole-sponsor" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} className="h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Skipper</Label>
                <Input data-testid="yole-skipper" value={form.skipper} onChange={(e) => setForm({ ...form, skipper: e.target.value })} className="h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Photo de la yole</Label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-20 h-20 rounded-2xl bg-slate-100 overflow-hidden flex-shrink-0 ring-2 ring-white shadow" style={{ backgroundColor: form.color }}>
                    {form.photo_url ? (
                      <img src={photoUrl(form.photo_url)} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/70 text-xs font-bold">
                        Aucune
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])}
                      data-testid="yole-photo-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        variant="outline"
                        className="rounded-full h-9 text-xs font-bold"
                        data-testid="yole-upload-photo"
                      >
                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ImagePlus className="h-3.5 w-3.5 mr-1" />}
                        {form.photo_url ? "Changer" : "Uploader"}
                      </Button>
                      {form.photo_url && (
                        <Button
                          type="button"
                          onClick={() => setForm({ ...form, photo_url: "" })}
                          variant="outline"
                          className="rounded-full h-9 text-xs font-bold text-red-500 border-red-200 hover:bg-red-50"
                          data-testid="yole-clear-photo"
                        >
                          Retirer
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">JPEG/PNG/WebP, 5 Mo max.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase font-bold text-slate-600">Couleur</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DEFAULT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 rounded-full transition ${form.color === c ? "ring-4 ring-[color:var(--deep)]/30 scale-110" : ""}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                  <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-16 h-8 p-1 rounded" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-yole" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-11">
                {editing ? "Enregistrer" : "Créer la yole"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {yoles.map((y) => (
          <div key={y.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
            {y.photo_url ? (
              <img
                src={photoUrl(y.photo_url)}
                alt={y.name}
                className="w-14 h-14 rounded-xl object-cover ring-2 ring-white shadow flex-shrink-0"
                style={{ backgroundColor: y.color }}
              />
            ) : (
              <div className="w-14 h-14 rounded-xl flex-shrink-0 ring-2 ring-white shadow flex items-center justify-center" style={{ backgroundColor: y.color }}>
                <Sailboat className="h-6 w-6 text-white/80" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{y.name}</div>
              <div className="text-xs text-slate-500 truncate">{y.sponsor || "—"}</div>
              <div className="text-[11px] text-slate-400 truncate">{y.skipper || "—"}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(y)} data-testid={`edit-yole-${y.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Edit className="h-4 w-4" /></button>
              <button onClick={() => remove(y.id)} data-testid={`delete-yole-${y.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {yoles.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-sm text-slate-500">
            Aucune yole. Ajoute-en pour commencer.
          </div>
        )}
      </div>
    </div>
  );
}

function StagesAdmin() {
  const [stages, setStages] = useState([]);
  const [yoles, setYoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(newStageForm());
  const [settleFor, setSettleFor] = useState(null);
  const [settleRanking, setSettleRanking] = useState([]);
  const [settleCapsized, setSettleCapsized] = useState([]);

  function newStageForm() {
    // Default: 09:30 Martinique local (races typically start around 09:30 AM local)
    return {
      name: "",
      stage_number: 1,
      date: new Date().toISOString().slice(0, 10),
      time: "09:30",
      start_village: "",
      end_village: "",
      stage_winner_odds: 3.0,
      capsize_yes_odds: 2.5,
      capsize_no_odds: 1.4,
    };
  }

  /** Build the ISO 8601 string with Martinique offset (-04:00, no DST).
   * The admin form always represents Martinique local time regardless of the browser TZ. */
  const buildMartiniqueIso = (dateYmd, timeHm) => {
    const t = (timeHm || "09:30").length === 5 ? `${timeHm}:00` : timeHm;
    return `${dateYmd}T${t}-04:00`;
  };
  /** Extract YYYY-MM-DD + HH:mm parts from any ISO string, expressed in Martinique local time. */
  const splitMartiniqueDate = (iso) => {
    if (!iso) return { date: "", time: "09:30" };
    const d = new Date(iso);
    const mtq = new Date(d.getTime() - 4 * 3600 * 1000);
    // format as YYYY-MM-DD / HH:mm using UTC of the shifted date
    const y = mtq.getUTCFullYear();
    const m = String(mtq.getUTCMonth() + 1).padStart(2, "0");
    const day = String(mtq.getUTCDate()).padStart(2, "0");
    const hh = String(mtq.getUTCHours()).padStart(2, "0");
    const mm = String(mtq.getUTCMinutes()).padStart(2, "0");
    return { date: `${y}-${m}-${day}`, time: `${hh}:${mm}` };
  };

  const load = async () => {
    const [s, y] = await Promise.all([api.get("/stages"), api.get("/yoles")]);
    setStages(s.data);
    setYoles(y.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...newStageForm(), stage_number: (stages.length || 0) + 1 });
    setOpen(true);
  };
  const startEdit = (s) => {
    setEditing(s);
    const { date, time } = splitMartiniqueDate(s.date);
    setForm({
      name: s.name,
      stage_number: s.stage_number,
      date,
      time,
      start_village: s.start_village,
      end_village: s.end_village,
      stage_winner_odds: s.stage_winner_odds,
      capsize_yes_odds: s.capsize_yes_odds,
      capsize_no_odds: s.capsize_no_odds,
    });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = {
        ...form,
        date: buildMartiniqueIso(form.date, form.time),
        stage_number: Number(form.stage_number),
        stage_winner_odds: Number(form.stage_winner_odds),
        capsize_yes_odds: Number(form.capsize_yes_odds),
        capsize_no_odds: Number(form.capsize_no_odds),
      };
      delete payload.time;
      if (editing) {
        await api.put(`/admin/stages/${editing.id}`, payload);
        toast.success("Étape modifiée");
      } else {
        await api.post("/admin/stages", payload);
        toast.success("Étape créée");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const closeStage = async (id) => {
    try {
      await api.post(`/admin/stages/${id}/close`);
      toast.success("Paris fermés");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reopenStage = async (s) => {
    const warning = s.status === "settled"
      ? `⚠️ RÉOUVERTURE D'UNE ÉTAPE RÉGLÉE\n\nRouvrir « ${s.name} » va :\n  • Rembourser les points attribués à tous les parieurs\n  • Remettre leurs pronostics en attente\n  • Effacer le classement final enregistré\n\nContinuer ?`
      : `Rouvrir les paris de « ${s.name} » ?`;
    if (!confirm(warning)) return;
    // If the current date is in the past, ask the admin for a new date
    const stageDate = new Date(s.date);
    let newDate = null;
    if (stageDate < new Date()) {
      const suggested = new Date(Date.now() + 24 * 3600 * 1000);
      // Format as YYYY-MM-DDTHH:mm in Martinique (input local -04:00)
      const mtq = new Date(suggested.getTime() - 4 * 3600 * 1000);
      const iso = mtq.toISOString().slice(0, 16);
      const val = prompt(
        "Nouvelle date/heure de départ (heure Martinique, format YYYY-MM-DDTHH:mm) :",
        iso,
      );
      if (!val) return;
      newDate = `${val}:00-04:00`;
    }
    try {
      const { data } = await api.post(`/admin/stages/${s.id}/reopen`, newDate ? { new_date: newDate } : {});
      if (data.refunded_users > 0) {
        toast.success(`Étape rouverte — points remboursés à ${data.refunded_users} joueur(s)`);
      } else {
        toast.success("Étape rouverte");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openSettle = (s) => {
    setSettleFor(s);
    setSettleRanking(s.final_ranking || []);
    setSettleCapsized(s.capsized_yole_ids || []);
  };
  const doSettle = async () => {
    if (settleRanking.length < 1) { toast.error("Ajoute au moins la yole gagnante"); return; }
    try {
      const res = await api.post(`/admin/stages/${settleFor.id}/settle`, {
        final_ranking: settleRanking,
        capsized_yole_ids: settleCapsized,
      });
      toast.success(`Étape réglée · ${res.data.rankings_settled} pronostics + ${res.data.legacy_bets_settled} paris traités`);
      setSettleFor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const moveInRanking = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= settleRanking.length) return;
    const next = [...settleRanking];
    [next[i], next[j]] = [next[j], next[i]];
    setSettleRanking(next);
  };
  const addToRanking = (yid) => setSettleRanking([...settleRanking, yid]);
  const removeFromRanking = (yid) => setSettleRanking(settleRanking.filter((id) => id !== yid));
  const remove = async (id) => {
    if (!confirm("Supprimer l'étape et rembourser les paris ?")) return;
    try {
      await api.delete(`/admin/stages/${id}`);
      toast.success("Étape supprimée");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const replace2026 = async () => {
    if (!confirm("Remplacer les étapes actuelles par les 8 étapes officielles 2026 ? Les étapes non réglées seront supprimées et les pronostics correspondants effacés.")) return;
    try {
      const { data } = await api.post("/admin/replace-stages-2026");
      toast.success(`${data.stages_added} étapes 2026 importées (${data.stages_removed} anciennes supprimées)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-slate-500">{stages.length} étapes</div>
        <div className="flex items-center gap-2">
          <Button
            onClick={replace2026}
            data-testid="replace-stages-2026"
            variant="outline"
            className="rounded-full h-10 font-bold text-xs border-[color:var(--ocean)] text-[color:var(--ocean)] hover:bg-[color:var(--ocean)]/10"
          >
            <Download className="h-4 w-4 mr-1" /> Étapes officielles 2026
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} data-testid="add-stage-btn" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-10">
                <Plus className="h-4 w-4 mr-1" /> Nouvelle étape
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold">
                {editing ? "Modifier l'étape" : "Nouvelle étape"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase font-bold text-slate-600">Nom</Label>
                  <Input data-testid="stage-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">N°</Label>
                  <Input data-testid="stage-number" type="number" min={1} value={form.stage_number} onChange={(e) => setForm({ ...form, stage_number: e.target.value })} className="h-11 rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Date de départ</Label>
                  <Input data-testid="stage-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Heure Martinique (départ)</Label>
                  <Input data-testid="stage-time" type="time" value={form.time || "09:30"} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-11 rounded-xl font-mono" />
                </div>
              </div>
              <div className="text-[10px] text-slate-500 -mt-1">
                Les paris se ferment automatiquement à cette heure locale (Martinique, UTC-4).
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Départ</Label>
                  <Input data-testid="stage-start" value={form.start_village} onChange={(e) => setForm({ ...form, start_village: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Arrivée</Label>
                  <Input data-testid="stage-end" value={form.end_village} onChange={(e) => setForm({ ...form, end_village: e.target.value })} className="h-11 rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Cote victoire</Label>
                  <Input type="number" step="0.1" min={1} value={form.stage_winner_odds} onChange={(e) => setForm({ ...form, stage_winner_odds: e.target.value })} className="h-11 rounded-xl font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Cote dessalage</Label>
                  <Input type="number" step="0.1" min={1} value={form.capsize_yes_odds} onChange={(e) => setForm({ ...form, capsize_yes_odds: e.target.value })} className="h-11 rounded-xl font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-bold text-slate-600">Cote sans dessal.</Label>
                  <Input type="number" step="0.1" min={1} value={form.capsize_no_odds} onChange={(e) => setForm({ ...form, capsize_no_odds: e.target.value })} className="h-11 rounded-xl font-mono" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-stage" className="rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold h-11">
                {editing ? "Enregistrer" : "Créer l'étape"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="space-y-3">
        {stages.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[color:var(--ocean)]/20 to-[color:var(--coral)]/20 flex items-center justify-center font-display font-black text-lg text-[color:var(--deep)] flex-shrink-0">
                {s.stage_number}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{s.name}</div>
                <div className="text-xs text-slate-500 truncate">{s.start_village} → {s.end_village} · {formatMartiniqueDateTime(s.date, { weekday: "short", month: "short", year: "numeric" })}</div>
                <div className="text-[11px] font-mono text-slate-400">
                  x{s.stage_winner_odds} · dessal. x{s.capsize_yes_odds} / x{s.capsize_no_odds}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${
                  s.status === "upcoming" ? "bg-[color:var(--palm)]/20 text-[color:var(--palm)]" :
                  s.status === "closed" ? "bg-amber-100 text-amber-700" :
                  "bg-slate-200 text-slate-600"
                }`}>{s.status}</span>
                <button onClick={() => startEdit(s)} data-testid={`edit-stage-${s.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Modifier date/heure"><Edit className="h-4 w-4" /></button>
                {s.status === "upcoming" && (
                  <button onClick={() => closeStage(s.id)} data-testid={`close-stage-${s.id}`} className="p-2 rounded-lg text-amber-600 hover:bg-amber-50" title="Fermer les paris"><Lock className="h-4 w-4" /></button>
                )}
                {(s.status === "closed" || s.status === "settled") && (
                  <button
                    onClick={() => reopenStage(s)}
                    data-testid={`reopen-stage-${s.id}`}
                    className="p-2 rounded-lg text-[color:var(--ocean)] hover:bg-cyan-50"
                    title={s.status === "settled" ? "Rouvrir (annule les points attribués)" : "Rouvrir les paris"}
                  >
                    <PlayCircle className="h-4 w-4" />
                  </button>
                )}
                {s.status !== "settled" && (
                  <Button onClick={() => openSettle(s)} data-testid={`settle-stage-${s.id}`} size="sm" className="rounded-full bg-[color:var(--sunshine)] hover:bg-[color:var(--sunshine)]/90 text-[color:var(--deep)] font-bold h-8">
                    <Trophy className="h-3.5 w-3.5 mr-1" /> Régler
                  </Button>
                )}
                <button onClick={() => remove(s.id)} data-testid={`delete-stage-${s.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {stages.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-sm text-slate-500">
            Aucune étape. Crée la première étape du tour.
          </div>
        )}
      </div>

      {/* Settle dialog */}
      <Dialog open={!!settleFor} onOpenChange={(o) => !o && setSettleFor(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">Régler l'étape</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase font-bold text-slate-600 mb-2 block">Classement final (de la 1re place vers la dernière)</Label>
              {settleRanking.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-2">Ajoute les yoles depuis la liste ci-dessous dans l'ordre d'arrivée.</div>
              ) : (
                <div className="space-y-1.5 mb-3">
                  {settleRanking.map((yid, i) => {
                    const y = yoles.find((yy) => yy.id === yid);
                    if (!y) return null;
                    return (
                      <div key={yid} className="flex items-center gap-2 p-2 rounded-xl bg-slate-50 border border-slate-100" data-testid={`settle-slot-${i}`}>
                        <div className="w-7 h-7 rounded-full bg-[color:var(--deep)] text-white font-mono font-black text-xs flex items-center justify-center">{i + 1}</div>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: y.color }} />
                        <div className="flex-1 text-sm font-bold truncate">{y.name}</div>
                        <button type="button" onClick={() => moveInRanking(i, -1)} disabled={i === 0} className="text-xs font-bold text-slate-500 disabled:opacity-30 px-1">↑</button>
                        <button type="button" onClick={() => moveInRanking(i, 1)} disabled={i === settleRanking.length - 1} className="text-xs font-bold text-slate-500 disabled:opacity-30 px-1">↓</button>
                        <button type="button" onClick={() => removeFromRanking(yid)} data-testid={`settle-remove-${yid}`} className="text-[10px] font-bold uppercase text-red-500 hover:underline">Retirer</button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {yoles.filter((y) => !settleRanking.includes(y.id)).map((y) => (
                  <button
                    key={y.id}
                    type="button"
                    onClick={() => addToRanking(y.id)}
                    data-testid={`settle-add-${y.id}`}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-slate-200 hover:border-[color:var(--coral)] text-xs font-bold"
                  >
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: y.color }} />
                    {y.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-bold text-slate-600 mb-2 block">Yoles ayant dessalé</Label>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-slate-200 rounded-xl p-3">
                {yoles.map((y) => (
                  <label key={y.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      data-testid={`settle-capsize-${y.id}`}
                      checked={settleCapsized.includes(y.id)}
                      onCheckedChange={(c) => {
                        if (c) setSettleCapsized([...settleCapsized, y.id]);
                        else setSettleCapsized(settleCapsized.filter((id) => id !== y.id));
                      }}
                    />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: y.color }} />
                    <span className="text-sm font-medium">{y.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doSettle} data-testid="confirm-settle" className="rounded-full bg-[color:var(--sunshine)] hover:bg-[color:var(--sunshine)]/90 text-[color:var(--deep)] font-bold h-11">
              Régler et distribuer les gains
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SeasonAdmin() {
  const [yoles, setYoles] = useState([]);
  const [season, setSeason] = useState({ settled: false });
  const [top1, setTop1] = useState("");
  const [top2, setTop2] = useState("");
  const [top3, setTop3] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [y, s] = await Promise.all([api.get("/yoles"), api.get("/season")]);
      setYoles(y.data);
      setSeason(s.data || { settled: false });
      if (s.data?.settled) {
        setTop1(s.data.top1_yole_id);
        setTop2(s.data.top2_yole_id);
        setTop3(s.data.top3_yole_id);
      }
    })();
  }, []);

  const yoleById = Object.fromEntries(yoles.map((y) => [y.id, y]));

  const submit = async () => {
    if (new Set([top1, top2, top3].filter(Boolean)).size !== 3) {
      toast.error("Choisis 3 yoles différentes");
      return;
    }
    if (!confirm("Régler le classement général ? Cette action est définitive.")) return;
    setLoading(true);
    try {
      const res = await api.post("/admin/season/settle", { top1_yole_id: top1, top2_yole_id: top2, top3_yole_id: top3 });
      toast.success(`Classement réglé · ${res.data.bets_settled} pronostics traités`);
      const s = await api.get("/season");
      setSeason(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl space-y-4">
      {season.settled && (
        <div className="bg-[color:var(--sunshine)]/15 border-2 border-[color:var(--sunshine)] rounded-2xl p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">Classement réglé</div>
          <div className="space-y-1 text-sm">
            <div>🥇 {yoleById[season.top1_yole_id]?.name}</div>
            <div>🥈 {yoleById[season.top2_yole_id]?.name}</div>
            <div>🥉 {yoleById[season.top3_yole_id]?.name}</div>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div>
          <Label className="text-xs uppercase font-bold text-slate-600">1re place</Label>
          <Select value={top1} onValueChange={setTop1} disabled={season.settled}>
            <SelectTrigger data-testid="season-top1" className="h-11 rounded-xl mt-1"><SelectValue placeholder="Yole" /></SelectTrigger>
            <SelectContent>{yoles.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold text-slate-600">2e place</Label>
          <Select value={top2} onValueChange={setTop2} disabled={season.settled}>
            <SelectTrigger data-testid="season-top2" className="h-11 rounded-xl mt-1"><SelectValue placeholder="Yole" /></SelectTrigger>
            <SelectContent>{yoles.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold text-slate-600">3e place</Label>
          <Select value={top3} onValueChange={setTop3} disabled={season.settled}>
            <SelectTrigger data-testid="season-top3" className="h-11 rounded-xl mt-1"><SelectValue placeholder="Yole" /></SelectTrigger>
            <SelectContent>{yoles.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {!season.settled && (
          <Button onClick={submit} disabled={loading} data-testid="settle-season" className="w-full rounded-full bg-[color:var(--sunshine)] hover:bg-[color:var(--sunshine)]/90 text-[color:var(--deep)] font-bold h-12">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trophy className="h-4 w-4 mr-2" /> Régler le classement général</>}
          </Button>
        )}
      </div>
    </div>
  );
}


function SettingsAdmin() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/live-tracker");
      setUrl(data.url || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/live-tracker", { url: url.trim() });
      toast.success(url.trim() ? "URL Georacing enregistrée" : "Suivi live désactivé");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-admin">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[color:var(--coral)] mb-1">
            <Radio className="h-3.5 w-3.5" /> Suivi en direct (Georacing)
          </div>
          <h2 className="brand-wordmark text-2xl text-[color:var(--deep)]">URL de l'iframe Georacing</h2>
          <p className="text-sm text-slate-500 mt-1">
            Colle ici l'URL du player Georacing pour l'édition en cours (ex.{" "}
            <code className="text-[11px] bg-slate-100 px-1 rounded">https://player.georacing.com/?event=102217&lang=fr</code>).
            Laisse vide pour masquer l'onglet "Live" côté utilisateur.
          </p>
        </div>

        <div>
          <Label className="text-xs uppercase font-bold text-slate-600">URL Georacing</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://player.georacing.com/?event=..."
            data-testid="live-tracker-url-input"
            disabled={loading}
            className="mt-1 h-11 rounded-xl font-mono text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={save}
            disabled={saving || loading}
            data-testid="live-tracker-save"
            className="rounded-full btn-cta h-11"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Enregistrer
          </Button>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="live-tracker-preview"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-[color:var(--ocean)] hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Tester dans un onglet
            </a>
          )}
        </div>

        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <strong>Astuce :</strong> chaque édition du Tour a un identifiant d'événement différent chez Georacing.
          Mets à jour cette URL au début de chaque saison.
        </div>
      </div>
    </div>
  );
}

